import { NodeEventsTypes } from '@treedis/tour-loader';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActionTypes, ClickAssetTypes } from '../types';
import { TourImage, TourObject, TourVSHtml, TourVideo } from '@/_models/virtualStaging/VSObject';
import { getClosestSweepFromObject } from '../utils/getClosestSweep';
import { rotateCameraToObject } from '../utils/rotateCameraToObject';
import { AudioModule, MusicObjectKey } from '@/components-new/common/AudioModule';
import { asyncStore } from '@/_helpers/base';
import { trackFlowStepComplete } from '../../../utils/flowAnalytics';

const audioHandler = AudioModule.getInstance();

const movementTypesMap = {
  flyover: 'FLY',
  instant: 'INSTANT',
  fadeout: 'FADEOUT',
};

const tagMoveMap = {
  instant: 'transition.instant',
  flyover: 'transition.fly',
  fadeout: 'transition.fade',
};

const mapConditoinAssetToMusicObjectKey = {
  polygon: MusicObjectKey.POLYGONS,
  tag: MusicObjectKey.TAGS,
  image: MusicObjectKey.VSIMAGES,
};

export const useConditions = ({
  showCaseTemp,
  showcase,
  tags,
  showElements,
  hideElements,
  moveToSweep,
  flow,
  setFlows,
  polygons,
  vsObjects,
  navigationManager,
  allSweeps,
  currentFloor,
  flowsVisibleAssets,
  handleEmitActionBySocket,
  socket,
}) => {
  const [steps, setSteps] = useState([]);
  const [stepsEdges, setStepsEdges] = useState([]);
  const [currentStep, setCurrentStep] = useState(null);
  const [distance, setDistance] = useState(null);
  const [conditionToHold, setConditionToHold] = useState(null);
  const startingStep = useMemo(() => steps?.find((s) => s.type === 'start_step'), [steps]);
  const [showFlowBar, setShowFlowBar] = useState(false);
  const [nextButtons, setNextButtons] = useState([]);
  const executedConditions = useRef(new Map());
  const videoStartedPlayingRef = useRef(false);
  const [stepStartTime, setStepStartTime] = useState(null);
  const activeTimersRef = useRef(new Map());
  const videoListenersRef = useRef(new Map());
  const objectListenersRef = useRef(new Map());
  const isTransitioningRef = useRef(false);
  const lastTransitionTimeRef = useRef(0);
  const TRANSITION_COOLDOWN = 800;

  useEffect(() => {
    (async () => {
      setCurrentStep(null);
      setSteps([]);
      setStepsEdges([]);
      setShowFlowBar(false);
      if (!flow) {
        return;
      }
      setSteps(flow?.steps);
      setStepsEdges(flow?.edges);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow?.id]);

  useEffect(() => {
    if (
      (startingStep && !currentStep && flow?.steps?.every((step) => !step.completed)) ||
      (!currentStep && flow?.restart)
    ) {
      setCurrentStep(startingStep);
    }
    if (!currentStep && flow?.steps?.some((step) => step.completed) && !flow?.restart) {
      const lastUnfinishedStep = flow?.steps
        ?.filter((step) => step.type === 'regular_step')
        .find((step) => !step.completed);
      const stepWithFullData = steps?.find((step) => step.id === lastUnfinishedStep.id);

      setCurrentStep(stepWithFullData);
    }
  }, [startingStep, currentStep, flow, steps]);

  useEffect(() => {
    if (!navigationManager || !navigationManager.component || !flow) return;

    navigationManager.component.inputs.onDistanceChanged = (d: number) => {
      setDistance(d);
    };

    // eslint-disable-next-line consistent-return
    return () => {
      navigationManager.reset();
      setDistance(null);
    };
  }, [navigationManager, currentStep, flow]);

  const handleTeleportToStep = async (step, shouldReset = true) => {
    if (step?.deepLink && step?.deepLink !== 'null' && !step?.posAssetType) {
      const urlParams = new URLSearchParams(step?.deepLink.replace(/(.+\?)/, '?'));
      const s = urlParams.get('s');
      const x = urlParams.get('x');
      const y = urlParams.get('y');
      console.log('BOAZ log test');
      await moveToSweep(
        s,
        { x: parseFloat(x), y: parseFloat(y) },
        movementTypesMap[step?.webMovement] || 'FLY',
        false,
      );
    } else if (step?.posAssetType === 'tag') {
      const tag = tags.find((t) => t.id === step?.posAssetId);
      const transition = tagMoveMap[step?.webMovement] || 'transition.fly';
      await showCaseTemp.showcase.Mattertag.navigateToTag(tag.sid, transition, true);
    } else {
      const mapTargetByAssetType = {
        polygon: polygons.find((p) => p.id === step?.posAssetId),
        image: vsObjects.find((obj) => obj instanceof TourImage && obj.id === step?.posAssetId),
        video: vsObjects.find((obj) => obj instanceof TourVideo && obj.id === step?.posAssetId),
        html: vsObjects.find((obj) => obj instanceof TourVSHtml && obj.id === step?.posAssetId),
        vsObject: vsObjects.find((obj) => obj instanceof TourObject && obj.id === step?.posAssetId),
      };
      const target = mapTargetByAssetType[step?.posAssetType];
      if (target) {
        const { closestSweep } = getClosestSweepFromObject({
          allSweeps,
          target: { ...target, floor: currentFloor || 0 },
        });
        if (closestSweep) {
          const transition = movementTypesMap[step?.webMovement] || 'FLY';
          await moveToSweep(closestSweep?.id, { x: 0, y: 0 }, transition, false);

          if (!asyncStore.vrIsOn) {
            // Non-VR mode: use regular camera rotation
            const cameraPose = await showcase.Camera.getPose();
            await rotateCameraToObject({
              showCaseTemp,
              currentPoint: {
                data: {
                  position: {
                    x: cameraPose?.position.x,
                    y: cameraPose?.position.y,
                    z: cameraPose?.position.z,
                  },
                },
              },
              nextPoint: {
                data: {
                  position: { x: target.positionX, y: target?.positionY, z: target.positionZ },
                },
              },
            });
          } else {
            // VR mode: dispatch event for VR rotation handling with target position
            const vrRotationEvent = new CustomEvent('vrFlowRotateToTarget', {
              detail: {
                targetSweep: closestSweep,
                target: target,
              },
            });
            document.dispatchEvent(vrRotationEvent);
          }
        }
      }
    }
    if (shouldReset) navigationManager.reset();
  };

  const renderPathToCurrentStep = async (step) => {
    if (step?.deepLink && step?.deepLink !== 'null' && !step?.posAssetType) {
      const urlParams = new URLSearchParams(step?.deepLink.replace(/(.+\?)/, '?'));
      const s = urlParams.get('s');
      const target = Array.from(allSweeps, ([, value]) => value)?.find(
        (item) => item.sweep_uuid === s || item.id === s,
      );
      if (target) {
        navigationManager.renderPath(navigationManager.currentSID, target?.id);
      }
    } else if (step?.posAssetType === 'tag') {
      const tag = tags.find((t) => t.id === step?.posAssetId);
      const { closestSweep } = getClosestSweepFromObject({ allSweeps, target: tag });
      if (closestSweep && closestSweep?.id !== navigationManager.currentSID) {
        navigationManager.renderPath(navigationManager.currentSID, closestSweep?.id, undefined, {
          ...tag,
          enableNavBar: false,
          enableTeleportBtn: false,
          autoNavigation: false,
        });
      }
    } else {
      const mapTargetByAssetType = {
        polygon: polygons.find((p) => p.id === step?.posAssetId),
        image: vsObjects.find((obj) => obj instanceof TourImage && obj.id === step?.posAssetId),
        video: vsObjects.find((obj) => obj instanceof TourVideo && obj.id === step?.posAssetId),
        html: vsObjects.find((obj) => obj instanceof TourVSHtml && obj.id === step?.posAssetId),
        vsObject: vsObjects.find((obj) => obj instanceof TourObject && obj.id === step?.posAssetId),
      };
      const target = mapTargetByAssetType[step?.posAssetType];

      if (target) {
        const { closestSweep } = getClosestSweepFromObject({
          allSweeps,
          target: { ...target, floor: currentFloor || 0 },
        });
        if (closestSweep && closestSweep?.id !== navigationManager.currentSID) {
          navigationManager.renderPath(navigationManager.currentSID, closestSweep?.id, undefined, {
            ...target,
            enableNavBar: false,
            enableTeleportBtn: false,
            autoNavigation: false,
          });
        }
      }
    }
  };

  const executeCondition = useCallback(
    async (condition, isFinishedFlow = false, isRevertStep = false) => {
      if (
        condition?.action !== ActionTypes.DELAY_FLOWBAR &&
        condition?.action !== ActionTypes.DELAY &&
        !isRevertStep
      ) {
        const currentConditions = executedConditions.current.get(currentStep?.id) || new Set();

        // Add the condition to the set
        currentConditions.add(condition);

        // Update the map with the new set of conditions
        executedConditions.current.set(currentStep?.id, currentConditions);
      }
      switch (condition.action) {
        case ActionTypes.DELAY_FLOWBAR:
          await new Promise((resolve) => {
            const timerId = setTimeout(resolve, condition.delay * 1000);
            if (!activeTimersRef.current.has(currentStep?.id)) {
              activeTimersRef.current.set(currentStep?.id, []);
            }
            activeTimersRef.current.get(currentStep?.id).push(timerId);
          });
          setShowFlowBar(true);
          break;
        case ActionTypes.DELAY:
          await new Promise((resolve) => {
            const timerId = setTimeout(resolve, condition.delay * 1000);
            if (!activeTimersRef.current.has(currentStep?.id)) {
              activeTimersRef.current.set(currentStep?.id, []);
            }
            activeTimersRef.current.get(currentStep?.id).push(timerId);
          });
          break;
        case ActionTypes.SHOW_ASSET:
          const vsImagesToReveal = condition?.vsItems
            ?.filter((item) => item.includes('image'))
            .map((item) => item.replace('image_', ''));
          const vsVideosToReveal = condition?.vsItems
            ?.filter((item) => item.includes('video'))
            .map((item) => item.replace('video_', ''));
          const vsHTMLsToReveal = condition?.vsItems
            ?.filter((item) => item.includes('html'))
            .map((item) => item.replace('html_', ''));
          const vsItemsToReveal = condition?.vsObjects;
          if (isFinishedFlow) {
            // If this is after a flow finish, we need to handle audio specially
            const currentlyPlayingObject = logCurrentlyPlayingVSObject();
            const playingObjectId = currentlyPlayingObject?.id;
            stopVSObjectAudio();

            await showElements({
              tagsToReveal: condition?.tags,
              polygonsToReveal: condition?.polygons,
              vsImagesToReveal,
              vsVideosToReveal,
              vsHTMLsToReveal,
              vsItemsToReveal,
            });

            // Then mute all objects except the one that was playing
            vsObjects.forEach((obj) => {
              if (obj instanceof TourObject && obj.node?.audio && obj.id !== playingObjectId) {
                obj.node.audio.pause();
                if (obj.node.audio.currentTime) {
                  obj.node.audio.currentTime = 0;
                }
                obj.node.audio.muted = true;
              }
            });
          } else {
            stopVSObjectAudio();
            await showElements({
              tagsToReveal: condition?.tags,
              polygonsToReveal: condition?.polygons,
              vsImagesToReveal,
              vsVideosToReveal,
              vsHTMLsToReveal,
              vsItemsToReveal,
            });
          }
          break;
        case ActionTypes.HIDE_ASSET:
          const vsImagesToHide = condition?.vsItems
            ?.filter((item) => item.includes('image'))
            .map((item) => item.replace('image_', ''));
          const vsVideosToHide = condition?.vsItems
            ?.filter((item) => item.includes('video'))
            .map((item) => item.replace('video_', ''));
          const vsHTMLsToHide = condition?.vsItems
            ?.filter((item) => item.includes('html'))
            .map((item) => item.replace('html_', ''));
          const vsItemsToHide = condition?.vsObjects;
          await hideElements(
            {
              hideTags: condition?.tags,
              polygonsToHide: condition?.polygons,
              vsImagesToHide,
              vsVideosToHide,
              vsHTMLsToHide,
              vsItemsToHide,
            },
            true,
          );
          break;
        case ActionTypes.SHOW_SWEEP:
          await showElements({ sweepsToReveal: condition?.sweeps });
          break;
        case ActionTypes.HIDE_SWEEP:
          await hideElements({ sweepsToHide: condition?.sweeps });
          break;
        case ActionTypes.POSITION_TO:
          if (condition.webMovement !== 'navigationPath') {
            handleTeleportToStep(
              {
                deepLink: condition.positionUrl,
                posAssetType: condition.posAssetType,
                posAssetId: condition.posAssetId,
                webMovement: condition.webMovement,
              },
              false,
            );
          } else {
            renderPathToCurrentStep({
              deepLink: condition.positionUrl,
              posAssetType: condition.posAssetType,
              posAssetId: condition.posAssetId,
              webMovement: condition.webMovement,
            });
          }
          break;
        default:
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStep],
  );

  const enableTriggers = useCallback(
    ({ assetType, assetId }) => {
      const currentStepDisabledTriggersIds = currentStep?.triggers
        ?.filter(
          (t) => t?.condition?.unlockAsset === assetType && t?.condition?.unlockAssetId === assetId,
        )
        ?.map((t) => t.id);
      if (currentStepDisabledTriggersIds?.length > 0) {
        setNextButtons((prev) =>
          prev.map((t) => ({
            ...t,
            disabled: t.disabled ? !currentStepDisabledTriggersIds?.includes(t.id) : t.disabled,
          })),
        );
      }
    },
    [currentStep],
  );

  const executeConditionsInOrder = async (
    conditions,
    isFinishedFlow = false,
    isRevertStep = false,
  ) => {
    const executeNext = async (index) => {
      if (index >= conditions?.length) {
        enableTriggers({ assetType: 'condition', assetId: conditions[0].conditionId });

        return Promise.resolve();
      }
      const condition = conditions[index];
      await executeCondition(condition, isFinishedFlow, isRevertStep);

      return executeNext(index + 1);
    };
    return executeNext(0);
  };

  const executeNextStepTrigger = async (trigger, stepStartTime = null) => {
    if (isTransitioningRef.current) {
      return;
    }

    const now = Date.now();
    const timeSinceLastTransition = now - lastTransitionTimeRef.current;
    if (timeSinceLastTransition < TRANSITION_COOLDOWN) {
      return;
    }

    isTransitioningRef.current = true;
    lastTransitionTimeRef.current = now;

    try {
      const nextStepEdge = stepsEdges?.find((edge) => edge.trigger === trigger.id);
      if (!nextStepEdge) {
        return;
      }

      const nextStep = steps?.find((step) => step.id === nextStepEdge?.target);
      if (!nextStep) {
        return;
      }

      removeAllListeners();
      if (currentStep?.id) {
        cleanupStepResources(currentStep.id);

        const currentStepExecutedConditions = executedConditions.current.get(currentStep.id);
        if (currentStepExecutedConditions) {
          const assetsToHide = {
            tags: new Set(),
            polygons: new Set(),
            vsItems: new Set(),
            vsObjects: new Set(),
          };

          currentStepExecutedConditions.forEach((condition: any) => {
            if (condition.action === ActionTypes.SHOW_ASSET) {
              condition.tags?.forEach((tag) => assetsToHide.tags.add(tag));
              condition.polygons?.forEach((polygon) => assetsToHide.polygons.add(polygon));
              condition.vsItems?.forEach((item) => assetsToHide.vsItems.add(item));
              condition.vsObjects?.forEach((obj) => assetsToHide.vsObjects.add(obj));
            }
          });

          if (
            assetsToHide.tags.size > 0 ||
            assetsToHide.polygons.size > 0 ||
            assetsToHide.vsItems.size > 0 ||
            assetsToHide.vsObjects.size > 0
          ) {
            await executeCondition({
              action: ActionTypes.HIDE_ASSET,
              tags: Array.from(assetsToHide.tags),
              polygons: Array.from(assetsToHide.polygons),
              vsItems: Array.from(assetsToHide.vsItems),
              vsObjects: Array.from(assetsToHide.vsObjects),
            });
          }
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 100));
      if (flow?.id && currentStep && currentStep.type === 'regular_step') {
        const duration = stepStartTime
          ? Math.round((Date.now() - stepStartTime) / 1000)
          : undefined;
        trackFlowStepComplete(flow.id, currentStep.id, duration);
      }

      if (trigger?.condition?.rows?.length > 0) {
        await executeConditionsInOrder(trigger?.condition?.rows);
      }
      setCurrentStep({ ...nextStep, triggeredBy: trigger.id });
      setSteps((prev) =>
        prev.map((s) => (s.id === nextStep.id ? { ...s, triggeredBy: trigger.id } : s)),
      );
      setFlows((prev) =>
        prev.map((f) => ({
          ...f,
          steps: f.steps.map((s) => (s.id === trigger.stepId ? { ...s, completed: true } : s)),
        })),
      );
    } catch (error) {
      console.error('Error during transition:', error);
    } finally {
      await new Promise((resolve) => setTimeout(resolve, 200));
      isTransitioningRef.current = false;
    }
  };

  // step conditions
  useEffect(() => {
    (async () => {
      if (currentStep) {
        if (currentStep.type === 'regular_step' && currentStep.webMovement === 'navigationPath') {
          await renderPathToCurrentStep(currentStep);
        }
        const stepConditions = currentStep?.conditions?.find((c) => c.action === 'start')?.rows;

        const currentStepHasDelayFlowbar = currentStep?.conditions?.find((c) =>
          c.rows?.find((r) => r.action === ActionTypes.DELAY_FLOWBAR),
        );
        if (currentStep.type === 'regular_step' && !currentStepHasDelayFlowbar) {
          setShowFlowBar(true);
        }

        if (stepConditions && stepConditions.length > 0) {
          const sortedConditions = [...stepConditions]
            .sort((a, b) => a.order - b.order)
            .map((c) => ({ ...c, isStartCondition: true }));
          await executeConditionsInOrder(sortedConditions);
        }
        if (currentStep?.type === 'start_step') {
          const nextStepEdge = stepsEdges?.find((edge) => edge.source === currentStep.id);
          const nextStep = steps?.find((step) => step.id === nextStepEdge?.target);

          // Use setTimeout to ensure the transition happens in the next tick
          // This prevents race conditions with analytics logging
          setTimeout(() => {
            setCurrentStep(nextStep);
          }, 0);
        }
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Track step start time for analytics
  useEffect(() => {
    if (currentStep && flow?.id) {
      // Only track analytics for regular steps, not start steps or finish steps
      if (currentStep.type === 'regular_step') {
        setStepStartTime(Date.now());
      }
    }
  }, [currentStep, flow?.id]);

  // reload audio
  const reloadAudio = useCallback(
    async (condition) => {
      const audioId =
        condition?.asset === 'polygon'
          ? polygons
              .find((p) => p.id === condition?.assetId)
              ?.polygonsActions?.find((a) => a.type === 'music')?.id
          : condition?.assetId;
      if (!audioId) {
        console.warn('Audio id not found');
      }

      const audio = audioHandler.getAudio(
        mapConditoinAssetToMusicObjectKey[condition?.asset],
        audioId,
      );

      if (audio && audio.readyState === 0) {
        audio.load();
      }
      return audio;
    },
    [polygons],
  );

  // 1
  const handleAssetClick = useCallback(
    async (assetType, assetId, preventEmit = false) => {
      if (!preventEmit) handleEmitActionBySocket('flow_asset_clicked', { assetType, assetId });
      const clickConditions = currentStep?.conditions?.filter((c) => c.action === 'click');
      let assetClickConditions = clickConditions
        ?.find((c) => c.asset === assetType && c.assetId === assetId)
        ?.rows?.sort((a, b) => a.order - b.order);
      const trigger = currentStep?.triggers?.find(
        (t) => t.asset === assetType && t.assetId === assetId,
      );
      const holdCondition = clickConditions?.find(
        (c) => c.assetId === assetId && c.after === 'actionFinished',
      );
      const triggerHold = trigger?.condition?.after === 'actionFinished' && trigger?.condition;
      const actionsStart = clickConditions?.find(
        (c) => c.assetId === assetId && c.after === 'actionsStart',
      );
      const triggerActionsStart =
        trigger?.condition?.after === 'actionsStart' && trigger?.condition;

      enableTriggers({ assetType, assetId });

      if (actionsStart || triggerActionsStart) {
        const combinedStartObj = { ...(actionsStart || {}), ...(triggerActionsStart || {}) };
        await reloadAudio(combinedStartObj);
      }

      if (holdCondition || triggerHold) {
        const combinedHoldObj = {
          ...(holdCondition || {}),
          ...(triggerHold || {}),
          rows: [...(holdCondition?.rows || []), ...(triggerHold?.rows || [])],
          trigger,
        };
        assetClickConditions = assetClickConditions?.filter(
          (c) => c.conditionId !== combinedHoldObj.id,
        );
        setConditionToHold(combinedHoldObj);
      }

      if (assetClickConditions) {
        await executeConditionsInOrder(assetClickConditions);
      }

      if (trigger && !holdCondition && !triggerHold) {
        await executeNextStepTrigger(
          {
            ...trigger,
            condition: { rows: [...(assetClickConditions || []), ...trigger?.condition?.rows] },
          },
          stepStartTime,
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStep, stepStartTime],
  );

  // 2
  const handleTagClick = useCallback(
    async (tagId) => {
      const tag: any = tags.find((t: any) => t.sid === tagId || t.defaultSid === tagId);

      handleAssetClick(ClickAssetTypes.Tag, tag?.id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tags, currentStep],
  );

  const assetEventMap = useMemo(() => {
    return {
      scenePolygon: {
        assetType: ClickAssetTypes.Polygon,
        eventType: NodeEventsTypes.POLYGON_CLICK,
        onClick: ({ assetId }) => handleAssetClick(ClickAssetTypes.Polygon, assetId),
      },
      sceneObject: {
        assetType: ClickAssetTypes.VsObject,
        eventType: NodeEventsTypes.OBJECT_CLICK,
        onClick: ({ data }) => handleAssetClick(ClickAssetTypes.VsObject, data.id),
      },
      sceneImage: {
        asssetType: ClickAssetTypes.Image,
        eventType: NodeEventsTypes.IMAGE_CLICK,
        onClick: ({ data }) => handleAssetClick(ClickAssetTypes.Image, data.id),
      },
      sceneVideo: {
        assetType: ClickAssetTypes.Video,
        eventType: NodeEventsTypes.VIDEO_CLICK,
        onClick: ({ data }) => handleAssetClick(ClickAssetTypes.Video, data.id),
      },
      sceneHTML: {
        assetType: ClickAssetTypes.HTML,
        eventType: NodeEventsTypes.HTML_CLICK,
        onClick: ({ data }) => handleAssetClick(ClickAssetTypes.HTML, data.id),
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // 3
  const handleVideoButtonClick = async ({ data, component, btnComponent }) => {
    component.video.addEventListener('play', () => {
      videoStartedPlayingRef.current = true;
    });

    if (btnComponent.inputs.type === 'play' && !videoStartedPlayingRef.current) {
      await handleAssetClick(ClickAssetTypes.Video, data.id);

      const videoStartPlayingConditions = currentStep?.conditions?.find(
        (c) => c.assetId === data.id && c.action === 'start_playing',
      )?.rows;
      if (videoStartPlayingConditions?.length) {
        await executeConditionsInOrder(videoStartPlayingConditions);
      }
    }
  };

  // click conditions
  useEffect(() => {
    if (!showCaseTemp || !showcase || !currentStep) return;
    showcase.on(showcase.Mattertag.Event.CLICK, handleTagClick);
    showCaseTemp.scene.sceneVideo.on(NodeEventsTypes.VIDEO_BUTTON_CLICK, handleVideoButtonClick);

    Object.entries(assetEventMap).forEach(([key, value]) => {
      showCaseTemp.scene[key].on(value.eventType, value.onClick);
    });

    // eslint-disable-next-line consistent-return
    return () => {
      showcase.off(showcase.Mattertag.Event.CLICK, handleTagClick);
      Object.entries(assetEventMap).forEach(([key, value]) => {
        showCaseTemp.scene[key].off(value.eventType, value.onClick);
      });
      showCaseTemp.scene.sceneVideo.off(NodeEventsTypes.VIDEO_BUTTON_CLICK, handleVideoButtonClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCaseTemp, showcase, currentStep]);

  // check for music action type delay
  useEffect(() => {
    const checkForAudioData = async (condition) => {
      const audio = await reloadAudio(condition);
      audio.addEventListener('ended', async () => {
        if (condition.trigger) {
          await executeNextStepTrigger(
            {
              ...condition.trigger,
              condition: { rows: condition?.rows },
            },
            stepStartTime,
          );
        } else {
          await executeConditionsInOrder(condition?.rows);
        }
        setConditionToHold(null);

        audio.pause();
        audio.currentTime = 0;
      });
    };

    if (conditionToHold) {
      checkForAudioData(conditionToHold);
    }

    return () => {
      setConditionToHold(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditionToHold]);

  const currentStepRef = useRef(currentStep);
  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  const removeAllListeners = useCallback(() => {
    const { videos, objects } = flowsVisibleAssets;

    if (videos?.length) {
      videos.forEach((video) => {
        if (video?.node?.video) {
          const listener = videoListenersRef.current.get(video.id);
          if (listener) {
            video.node.video.removeEventListener('ended', listener);
            videoListenersRef.current.delete(video.id);
          }
        }
      });
    }

    if (objects?.length) {
      objects.forEach((obj) => {
        if (obj?.node?.mixer) {
          const listener = objectListenersRef.current.get(obj.id);
          if (listener) {
            obj.node.mixer.removeEventListener('finished', listener);
            objectListenersRef.current.delete(obj.id);
          }
        }
      });
    }
  }, [flowsVisibleAssets]);

  const executeFinishPlayingConditions = useCallback(
    (obj, type, stepId) => async () => {
      if (currentStepRef.current?.id !== stepId) {
        return;
      }

      const finishPlayingConditions = currentStepRef.current?.conditions?.find(
        (c) => c.asset === type && c.assetId === obj.id && c.action === 'finish_playing',
      )?.rows;
      videoStartedPlayingRef.current = false;
      if (finishPlayingConditions?.length) {
        await executeConditionsInOrder(finishPlayingConditions);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [executeConditionsInOrder],
  );

  useEffect(() => {
    if (!currentStep) return;

    const finishedMedia = new Set();

    const checkInterval = setInterval(() => {
      if (currentStepRef.current?.id !== currentStep.id) {
        clearInterval(checkInterval);
        return;
      }

      if (isTransitioningRef.current) {
        return;
      }

      const videosWithConditions = vsObjects?.filter((obj) => {
        if (!(obj instanceof TourVideo)) return false;

        const hasDirectCondition = currentStep?.conditions?.some(
          (c) => c.asset === 'video' && c.assetId === obj.id && c.action === 'finish_playing',
        );

        if (hasDirectCondition) return true;

        if (obj.vsVideoId) {
          const videosWithSameVsId = vsObjects.filter(
            (v) => v instanceof TourVideo && v.vsVideoId === obj.vsVideoId,
          );
          return videosWithSameVsId.some((v) =>
            currentStep?.conditions?.some(
              (c) => c.asset === 'video' && c.assetId === v.id && c.action === 'finish_playing',
            ),
          );
        }

        return false;
      });

      videosWithConditions?.forEach((video) => {
        const videoElement = video.node?.video;
        if (!videoElement) return;

        const mediaKey = `video-${video.id}`;

        const hasFinished =
          videoElement.ended ||
          (videoElement.duration > 0 &&
            Math.abs(videoElement.currentTime - videoElement.duration) < 0.5);

        if (hasFinished && !finishedMedia.has(mediaKey)) {
          finishedMedia.add(mediaKey);

          executeFinishPlayingConditions(video, 'video', currentStep.id)();
        }
      });

      const objectsWithConditions = vsObjects?.filter((obj) => {
        if (!(obj instanceof TourObject)) return false;
        return currentStep?.conditions?.some(
          (c) => c.asset === 'vsObject' && c.assetId === obj.id && c.action === 'finish_playing',
        );
      });

      objectsWithConditions?.forEach((obj) => {
        const audioElement = obj.node?.audio;
        if (!audioElement) return;

        const mediaKey = `object-${obj.id}`;

        // Check if audio has finished playing
        const hasFinished =
          audioElement.ended ||
          (audioElement.duration > 0 &&
            Math.abs(audioElement.currentTime - audioElement.duration) < 0.5);

        if (hasFinished && !finishedMedia.has(mediaKey)) {
          finishedMedia.add(mediaKey);

          executeFinishPlayingConditions(obj, 'vsObject', currentStep.id)();
        }
      });
    }, 200);

    // eslint-disable-next-line consistent-return
    return () => {
      clearInterval(checkInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, vsObjects, executeFinishPlayingConditions]);

  useEffect(() => {
    if (currentStep) {
      const triggers = currentStep?.triggers
        ?.filter((t) => t.type === ClickAssetTypes.Button)
        ?.map((t) => ({
          ...t,
          disabled: !!t?.condition && !!t.condition.unlockAsset && !!t.condition.unlockAssetId,
        }));
      setNextButtons(triggers);
    }
  }, [currentStep]);

  socket.on('flow_asset_clicked', ({ assetType, assetId }) => {
    handleAssetClick(assetType, assetId, true);
  });

  // Enhance the stopVSObjectAudio function to be more reusable
  const stopVSObjectAudio = useCallback(
    (specificObjects = null) => {
      const objectsToProcess = specificObjects || vsObjects;
      if (objectsToProcess?.length) {
        objectsToProcess.forEach((obj) => {
          if (obj instanceof TourObject && obj.node?.mixer) {
            if (obj.node.audio) {
              console.log('Stopping audio for VS object:', obj.id);

              // Pause the audio
              obj.node.audio.pause();

              // Reset the audio to the beginning
              if (obj.node.audio.currentTime) {
                obj.node.audio.currentTime = 0;
              }

              obj.node.audio.muted = true;

              if (obj.node.audioPlayed) {
                obj.node.audioPlayed = false;
              }
            }
          }
        });
      }

      return true;
    },
    [vsObjects],
  );

  const logCurrentlyPlayingVSObject = useCallback(() => {
    if (!vsObjects || vsObjects.length === 0) {
      return null;
    }

    const playingObjects = vsObjects.filter((obj) => {
      if (obj instanceof TourObject && obj.node?.audio) {
        return (
          !obj.node.audio.paused &&
          !obj.node.audio.ended &&
          obj.node.audio.currentTime > 0 &&
          !obj.node.audio.muted
        );
      }
      return false;
    });

    if (playingObjects.length === 0) {
      return null;
    }

    return playingObjects[0];
  }, [vsObjects]);

  const cleanupStepResources = useCallback(
    (stepId) => {
      const timers = activeTimersRef.current.get(stepId);
      if (timers && timers.length > 0) {
        timers.forEach((timerId) => clearTimeout(timerId));
        activeTimersRef.current.delete(stepId);
      }
      const { videos, objects } = flowsVisibleAssets;
      if (videos?.length) {
        videos.forEach((video) => {
          if (video?.node?.video) {
            const isPlaying = !video.node.video.paused && video.node.video.currentTime > 0;
            if (isPlaying) {
              video.node.video.pause();
              video.node.video.currentTime = 0;
            }
          }
        });
      }

      if (objects?.length) {
        objects.forEach((obj) => {
          if (obj?.node?.audio) {
            const isPlaying = !obj.node.audio.paused && obj.node.audio.currentTime > 0;
            if (isPlaying) {
              obj.node.audio.pause();
              obj.node.audio.currentTime = 0;
            }
          }
        });
      }
      setConditionToHold(null);
    },
    [flowsVisibleAssets],
  );

  return {
    stepsEdges,
    currentStep,
    steps,
    setCurrentStep,
    executeNextStepTrigger,
    distance,
    handleTeleportToStep,
    executeConditionsInOrder,
    executedConditions,
    showFlowBar,
    setShowFlowBar,
    nextButtons,
    stopVSObjectAudio,
    cleanupStepResources,
    removeAllListeners,
    isTransitioningRef,
    lastTransitionTimeRef,
    TRANSITION_COOLDOWN,
  };
};
